import { observer } from 'mobx-react';

import { Button } from 'react-bootstrap';
import * as style from './ExportDeal.module.less';
import html2canvas from 'html2canvas';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Entry, useStore, useClientSettingStore } from '@assets/model/Store';
import flightline_logo from '@assets/component/Header/new_logo.png';
import _ from 'lodash';
import { sanitizeName } from '@assets/firebase/FirebaseAdapater';

pdfMake.addVirtualFileSystem(pdfFonts);

// Helper function to strip all citations and markdown links from text
const stripCitationsAndMarkdown = (text: string): string => {
    if (!text) return text;

    let cleanText = text;

    // Remove superscript citations (¹²³⁴⁵⁶⁷⁸⁹⁰)
    cleanText = cleanText.replace(/[\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u2070]+/g, '');

    // Remove bracket citations [1], [2], etc.
    cleanText = cleanText.replace(/\[\d+\]/g, '');

    // Remove markdown links [Text](url) -> Text
    cleanText = cleanText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    // Remove standalone "Source" word at the end of sentences or text
    cleanText = cleanText.replace(/\.\s*Source\s*$/gi, '.');
    cleanText = cleanText.replace(/\s+Source\s*$/gi, '');

    return cleanText.trim();
};

export const ExportDeal = observer(() => {
    const store = useStore();
    const clientStore = useClientSettingStore();
    const do_we_have_pdf = store.query.report_has_pdf;
    const enableSourcing = clientStore.enableSourcing;

    const create_pdf = async () => {
        async function urlToBase64(url) {
            const response = await fetch(url);
            const blob = await response.blob();

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result); // base64 string
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        }

        const dash_line = {
            width: 500,
            height: 5,
            canvas: [
                {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 500,
                    y2: 0, // adjust width to text length
                    lineWidth: 1.0,
                    lineColor: '#d0d5dd',
                    //lineColor: 'black',
                    dash: {
                        length: 2,
                        space: 2
                    } // dashed pattern
                }
            ]
        };

        // Helper to parse text with citations into pdfmake format
        const parseTextWithCitations = (data: any) => {
            if (!data) return { text: [], sources: [] };

            // If sourcing is disabled, strip all citations and return plain text
            if (!enableSourcing) {
                const cleanText = typeof data === 'string'
                    ? stripCitationsAndMarkdown(data)
                    : stripCitationsAndMarkdown(data.text || '');
                return { text: [{ text: cleanText }], sources: [] };
            }

            // If it's just a string, parse markdown links and citations
            if (typeof data === 'string') {
                const parts = [];
                let lastIndex = 0;

                // Parse both citation markers [1] and markdown links [text](url)
                const combinedRegex = /\[(\d+)\]|\[([^\]]+)\]\(([^)]+)\)/g;
                let match;

                while ((match = combinedRegex.exec(data)) !== null) {
                    // Add text before match
                    if (match.index > lastIndex) {
                        parts.push({ text: data.slice(lastIndex, match.index) });
                    }

                    if (match[1]) {
                        // It's a citation marker [1], [2], etc.
                        parts.push({
                            text: match[1],
                            fontSize: 8,
                            color: '#4a90e2',
                            sup: true,
                            bold: true
                        });
                    } else if (match[2] && match[3]) {
                        // It's a markdown link [text](url)
                        parts.push({
                            text: match[2],
                            link: match[3],
                            color: '#4a90e2',
                            decoration: 'underline',
                            fontSize: 10
                        });
                    }

                    lastIndex = match.index + match[0].length;
                }

                // Add remaining text
                if (lastIndex < data.length) {
                    parts.push({ text: data.slice(lastIndex) });
                }

                return { text: parts.length > 0 ? parts : [{ text: data }], sources: [] };
            }

            // If it has text and sources, parse citations
            if (data.text && data.sources) {
                const textContent = data.text;
                const sources = data.sources || [];

                // Split text by citation markers [1], [2], etc.
                const parts = [];
                let lastIndex = 0;
                const citationRegex = /\[(\d+)\]/g;
                let match;

                while ((match = citationRegex.exec(textContent)) !== null) {
                    // Add text before citation
                    if (match.index > lastIndex) {
                        parts.push({ text: textContent.slice(lastIndex, match.index) });
                    }
                    // Add citation as superscript
                    const citationNum = match[1];
                    parts.push({
                        text: citationNum,
                        fontSize: 8,
                        color: '#4a90e2',
                        sup: true,
                        bold: true
                    });
                    lastIndex = match.index + match[0].length;
                }

                // Add remaining text
                if (lastIndex < textContent.length) {
                    parts.push({ text: textContent.slice(lastIndex) });
                }

                return { text: parts.length > 0 ? parts : [{ text: textContent }], sources };
            }

            return { text: [{ text: String(data) }], sources: [] };
        };

        // Helper to parse Diligence description with special tags
        const parseDiligenceDescription = (text: string) => {
            if (!text) return null;

            const detailedMatch = text.match(/\*\*\[DETAILED\]\*\*(.*?)(?=\*\*\[|$)/s);
            const relevanceMatch = text.match(/\*\*\[RELEVANCE\]\*\*(.*?)(?=\*\*\[|$)/s);
            const believesMatch = text.match(/\*\*\[BELIEVES\]\*\*(.*?)(?=\*\*\[|$)/s);

            return {
                detailed: detailedMatch?.[1]?.trim() || '',
                relevance: relevanceMatch?.[1]?.trim() || '',
                believes: believesMatch?.[1]?.trim() || ''
            };
        };

        // Helper to create sources section for pdfmake
        const createSourcesList = (sources: any[]) => {
            if (!enableSourcing || !sources || sources.length === 0) return [];

            const sourceItems = sources.map(source => {
                const sourceId = source.id || 0;
                const sourceName = source.name || 'Source';
                const sourceUrl = source.url;

                return {
                    text: [
                        { text: `${sourceId}. `, fontSize: 10 },
                        sourceUrl
                            ? { text: sourceName, link: sourceUrl, color: '#4a90e2', decoration: 'underline', fontSize: 10 }
                            : { text: sourceName, fontSize: 10, italics: true, color: '#667085' }
                    ],
                    margin: [0, 2, 0, 2]
                };
            });

            return [
                { text: '', margin: [0, 10, 0, 5] },
                { text: 'Sources:', fontSize: 11, bold: true, color: '#667085' },
                { text: '', margin: [0, 3, 0, 0] },
                ...sourceItems,
                { text: '', margin: [0, 0, 0, 20] }  // Extra spacing after sources
            ];
        };

        const createTextSection = (
            title: string,
            data: any,
            breakLine: string = 'avoid'
        ) => {
            const { text, sources } = parseTextWithCitations(data);

            if (!text || (Array.isArray(text) && text.length === 0)) {
                return { text: '', pageBreak: 'avoid' };
            }

            const stackItems = [
                {
                    text: title,
                    style: 'section_title'
                },
                _.cloneDeep(dash_line),
                {
                    text: text,
                    style: 'text_content'
                }
            ];

            // Add sources if they exist
            if (sources && sources.length > 0) {
                stackItems.push(...createSourcesList(sources));
            }

            return {
                stack: stackItems,
                pageBreak: breakLine
            };
        };

        const createChartSection = async (title: string, elm_id: string) => {
            const element = document.getElementById(elm_id);
            const chart = await html2canvas(element, {
                backgroundColor: null,
                scale: 2
            });
            const imgData = chart.toDataURL('image/png');
            return {
                stack: [
                    {
                        text: title,
                        style: 'section_title'
                    },
                    _.cloneDeep(dash_line),
                    { image: imgData, width: 500 } // you can set width/height
                ],
                pageBreak: 'before'
            };
        };

        const createColumnsSection = (title, entries: Entry[], includeSources: boolean = false) => {
            if (entries.length == 0) {
                return { text: '' };
            }

            const columns = [];
            const LINE_SIZE = 2;
            for (var i = 0; i < entries.length; i += LINE_SIZE) {
                var gen_cols = {
                    columns: [],
                    columnGap: 20,
                    pageBreak: 'avoid'
                };

                for (var y = 0; y < LINE_SIZE; y++) {
                    const accessAt = i + y;
                    if (entries.length > accessAt) {
                        const entry = entries[accessAt];
                        const { text: descriptionText } = parseTextWithCitations(entry.description);

                        const itemStack = [
                            {
                                text: entry.title,
                                style: 'text_content',
                                margin: [5, 5, 5, 0]
                            },
                            {
                                text: descriptionText,
                                style: 'text_content_small',
                                margin: [10, 5, 5, enableSourcing && includeSources && entry.sources ? 10 : 20]
                            }
                        ];

                        // Add sources if enableSourcing and includeSources flag is true and sources exist
                        if (enableSourcing && includeSources && entry.sources && entry.sources.length > 0) {
                            const sourcesItems = entry.sources.map(source => {
                                const sourceId = source.id || 0;
                                const sourceName = source.name || 'Source';
                                const sourceUrl = source.url;

                                return {
                                    text: [
                                        { text: `${sourceId}. `, fontSize: 9 },
                                        sourceUrl
                                            ? { text: sourceName, link: sourceUrl, color: '#4a90e2', decoration: 'underline', fontSize: 9 }
                                            : { text: sourceName, fontSize: 9, italics: true, color: '#667085' }
                                    ],
                                    margin: [10, 2, 5, 2]
                                };
                            });

                            itemStack.push(
                                { text: 'Sources:', fontSize: 10, bold: true, color: '#667085', margin: [10, 5, 5, 3] },
                                ...sourcesItems,
                                { text: '', margin: [0, 0, 0, 15] }
                            );
                        }

                        gen_cols.columns.push({
                            stack: itemStack,
                            width: '*'
                        });
                    } else {
                        gen_cols.columns.push({
                            text: '',
                            style: 'text_content',
                            width: '*'
                        });
                    }
                }
                columns.push(gen_cols);
            }

            return {
                stack: [
                    {
                        text: title,
                        style: 'section_title'
                    },
                    _.cloneDeep(dash_line),
                    {
                        text: '',
                        margin: [0, 20, 0, 0]
                    },
                    ...columns
                ],
                pageBreak: 'before'
            };
        };

        const createDiligenceSection = (title: string, entries: Entry[]) => {
            if (entries.length == 0) {
                return { text: '' };
            }

            const items = [];

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const parsed = parseDiligenceDescription(entry.description);

                if (!parsed) continue;

                const itemStack = [];

                // Title
                itemStack.push({
                    text: entry.title,
                    style: 'text_content',
                    bold: true,
                    margin: [0, i === 0 ? 20 : 30, 0, 10]
                });

                // Diligence Objective
                if (parsed.detailed) {
                    const { text: detailedText, sources: detailedSources } = parseTextWithCitations(parsed.detailed);
                    itemStack.push({
                        text: [
                            { text: 'Diligence Objective: ', bold: true, fontSize: 11 },
                            ...detailedText
                        ],
                        style: 'text_content_small',
                        margin: [0, 0, 0, 8]
                    });
                }

                // Deal Relevance
                if (parsed.relevance) {
                    const { text: relevanceText, sources: relevanceSources } = parseTextWithCitations(parsed.relevance);
                    itemStack.push({
                        text: [
                            { text: 'Deal Relevance: ', bold: true, fontSize: 11 },
                            ...relevanceText
                        ],
                        style: 'text_content_small',
                        margin: [0, 0, 0, 8]
                    });
                }

                // Need-to-Believe
                if (parsed.believes) {
                    const { text: believesText, sources: believesSources } = parseTextWithCitations(parsed.believes);
                    itemStack.push({
                        text: [
                            { text: 'Need-to-Believe: ', bold: true, fontSize: 11 },
                            ...believesText
                        ],
                        style: 'text_content_small',
                        margin: [0, 0, 0, 8]
                    });
                }

                // Collect all sources from all three sections
                const allSources = [];
                const addSources = (text: string) => {
                    const citationRegex = /\[(\d+)\]/g;
                    let match;
                    const citationNumbers = new Set();
                    while ((match = citationRegex.exec(text)) !== null) {
                        citationNumbers.add(parseInt(match[1]));
                    }
                    return Array.from(citationNumbers).sort((a, b) => a - b);
                };

                // Extract citation numbers from original text
                const originalText = entry.description || '';
                const citationNumbers = addSources(originalText);

                // If there are sources in the item, add them
                if (entry.sources && entry.sources.length > 0) {
                    const sourcesListItems = createSourcesList(entry.sources);
                    if (sourcesListItems.length > 0) {
                        itemStack.push(...sourcesListItems);
                    }
                }

                items.push({
                    stack: itemStack,
                    pageBreak: 'avoid'
                });
            }

            return {
                stack: [
                    {
                        text: title,
                        style: 'section_title'
                    },
                    _.cloneDeep(dash_line),
                    ...items
                ],
                pageBreak: 'before'
            };
        };
        const flowise_data = store.report_flowise ?? {
            opportunity: '',
            business_overview: '',
            market_description: '',
            market_size: '',

            core_product_services: [],
            key_customers: [],
            competitor_segments: [],
            what_we_like: [],
            key_risks: [],
            diligence: []
        };
        const q = store.query;
        const logo_base64 = await urlToBase64(flightline_logo);

        const stack_items = [];
        stack_items.push({
            text: store.company_name,
            style: 'company_name'
        });

        stack_items.push(
            createTextSection(
                'Summary of the Opportunity',
                flowise_data.opportunity
            )
        );
        if (do_we_have_pdf) {
            // Use business_overview with citations if available, otherwise fallback to companyOverview
            const businessOverviewData = flowise_data.business_overview || store.companyOverview;
            stack_items.push(
                createTextSection('Business Overview', businessOverviewData)
            );
            stack_items.push(
                await createChartSection('Financial Overview', 'revenue-chart')
            );
        }
        stack_items.push(
            createColumnsSection(
                'Core Products / Services',
                flowise_data.core_product_services ?? []
            )
        );
        stack_items.push(
            createColumnsSection(
                'Key Customers',
                flowise_data.key_customers ?? []
            )
        );
        if (do_we_have_pdf) {
            stack_items.push(
                await createChartSection('Score Summary', 'score-chart')
            );
            stack_items.push(
                await createChartSection('Benchmarking', 'benchmarking-chart')
            );
        }
        stack_items.push(
            createTextSection(
                'Market Description',
                flowise_data.market_description,
                'before'
            )
        );
        stack_items.push(
            createTextSection(
                'Market Size & Growth',
                flowise_data.market_size
            )
        );
        stack_items.push(
            createColumnsSection(
                'Competitor Segments',
                flowise_data.competitor_segments ?? []
            )
        );
        stack_items.push(
            createColumnsSection(
                'What We Like',
                flowise_data.what_we_like ?? [],
                true  // Include sources
            )
        );
        stack_items.push(
            createColumnsSection(
                'Key Risks',
                flowise_data.key_risks ?? [],
                true  // Include sources
            )
        );
        stack_items.push(
            createDiligenceSection(
                'Focus Areas For Due Diligence',
                flowise_data.diligence ?? []
            )
        );

        var dd = {
            content: [
                {
                    stack: stack_items
                }
            ],
            header: function (currentPage, pageCount) {
                return {
                    columns: [
                        { text: '' }, // empty left column
                        {
                            image: logo_base64,
                            width: 100,
                            alignment: 'right',
                            margin: [0, 30, 20, 0]
                        }
                    ]
                };
            },
            footer: function (currentPage, pageCount) {
                return {
                    text: 'Page ' + currentPage + ' of ' + pageCount,
                    alignment: 'center',
                    margin: [0, 0, 0, 20]
                };
            },
            styles: {
                company_name: {
                    color: '#102442',
                    fontSize: 22,
                    bold: true,
                    margin: [0, 0, 0, 30]
                },
                section_title: {
                    //font: 'Lust',
                    color: '#243a5c',
                    fontSize: 18,
                    italics: true,
                    margin: [0, 0, 0, 2]
                },
                text_content: {
                    margin: [0, 15, 0, 20],
                    fontSize: 12
                },

                text_content_small: {
                    margin: [0, 10, 0, 0],
                    fontSize: 10
                }
            }
        };

        const baseUrl = document.location?.origin;
        const fonts = {
            Lust: {
                normal: baseUrl + '/fonts/Lust-Text-Medium.otf',
                italics: baseUrl + '/fonts/Lust-Text-Italic.otf'
            }
        };

        const filename = sanitizeName(store.company_name) + '.pdf';
        pdfMake.createPdf(dd).download(filename);
    };

    return (
        <Button
            variant="light"
            className={style.exportButton}
            onClick={create_pdf}
        >
            <i className="bi bi-download"></i>
            Export
        </Button>
    );
});
